import React, { useState, useEffect, useMemo } from "react";
import { ReceiptText } from "lucide-react"; 
import { getDataByAll as getWorkshops } from "../../../../api/workshopApi"; 

import SectionHeader from "../../../Common/SectionHeader";
import SectionFilter from "../../../Common/SectionFilter";
import DateRangeFilter from "../../../Common/DateRangeFilter"; 
import SectionTable from "../../../Common/SectionTable";
import Pagination from "../../../Common/SectionTablePagination";
import ExportDataButton from "../../../Common/ExportDataButton";
import RefreshButton from "../../../Common/RefreshButton";

const WorkshopTransactionIndex = () => {
  const [workshops, setWorkshops] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  const [searchWorkshop, setSearchWorkshop] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [workshopPage, setWorkshopPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = () => {
    setLoading(true);
    getWorkshops()
      .then((data) => {
        setWorkshops(data || []); 
      })
      .catch((err) => console.error("Error fetching workshops:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const processedWorkshops = useMemo(() => {
    const now = new Date(); 

    return (workshops || [])
      .filter((ws) => ws.title.toLowerCase().includes(searchWorkshop.toLowerCase()))
      .map((ws) => {
        // --- LOGIKA STATUS DINAMIS ---
        let statusLabel = "";
        let statusColor = "";

        const timeParts = ws.time.split(" - ");
        const startTime = timeParts[0];
        const endTime = timeParts[1];

        const startFullDate = new Date(`${ws.startDate}T${startTime}`);
        const endFullDate = new Date(`${ws.endDate}T${endTime}`);

        if (now < startFullDate) {
          statusLabel = "Akan Datang";
          statusColor = "text-blue-600 bg-blue-50 dark:bg-blue-900/20";
        } else if (now > endFullDate) {
          statusLabel = "Selesai";
          statusColor = "text-stone-400 bg-stone-100 dark:bg-white/5";
        } else {
          statusLabel = "Sedang Berlangsung";
          statusColor = "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20";
        }

        const soldTickets = ws.soldTickets || 0; 
        const remainingTickets = ws.quota - soldTickets;
        
        return { ...ws, soldTickets, remainingTickets, statusLabel, statusColor };
      });
  }, [workshops, searchWorkshop]);

  const currentWorkshops = processedWorkshops.slice(
    (workshopPage - 1) * itemsPerPage, 
    workshopPage * itemsPerPage
  );

  const workshopHeaders = [
    { label: "Workshop Title" },
    { label: "Speaker" },
    { label: "Schedule & Time", className: "text-center" },
    { label: "Price", className: "text-right" },
    { label: "Capacity", className: "text-center" },
    { label: "Sold", className: "text-center" },
    { label: "Remaining", className: "text-center" },
    { label: "Status", className: "text-center" },
  ];

  return (
    <div className="p-6 bg-white dark:bg-slate-900 rounded-[1rem] shadow-sm border border-stone-100 dark:border-white/5 font-sans">   
      {/* HEADER AREA */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
        <SectionHeader 
          title="Workshop Transactions"
          subtitle="Monitoring sales performance and seat availability"
          Icon={ReceiptText}
        />
        <div className="flex items-center gap-3 w-full md:w-auto">
          <RefreshButton onClick={fetchData} isLoading={loading} className="w-full md:w-auto md:px-6 py-3" />
          <ExportDataButton onClick={() => console.log("Exporting...")} className="w-full md:w-auto md:px-10 py-3" />
        </div>
      </div>

      {/* FILTER PENCARIAN DATE */}
      <div className="space-y-10 mb-10 pt-6 border-t border-stone-100 dark:border-white/5">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-11 xl:col-span-8">
            <DateRangeFilter 
              currentStartDate={startDate}
              currentEndDate={endDate}
              onApply={(start, end) => { setStartDate(start); setEndDate(end); }}
              onClear={() => { setStartDate(""); setEndDate(""); }}
            />
          </div>
        </div>
      </div>

      {/* FILTER PENCARIAN TEXT */}
      <div className="space-y-10 mb-10 pt-6 border-t border-stone-100 dark:border-white/5">
        <div className="lg:col-span-5">
          <SectionFilter 
            searchTerm={searchWorkshop}
            onSearchChange={setSearchWorkshop}
            placeholder="Search workshop title..."
            columns={[{label: "None"}]} 
            selectedColumn={"None"}
            onColumnSelect={() => {}}
          />
        </div>
      </div>

      {/* SECTION TABLE */}
      <SectionTable 
        headers={workshopHeaders}
        data={currentWorkshops}
        loading={loading}
        renderRow={(ws) => (
          <>
            <td className="px-6 py-5 rounded-l-xl">
              <p className="text-xs font-black text-stone-800 dark:text-white uppercase tracking-tight">{ws.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[9px] bg-stone-100 dark:bg-white/5 text-stone-500 px-1.5 py-0.5 rounded font-bold uppercase">ID: {ws.id}</span>
              </div>
            </td>

            <td className="px-6 py-5">
              <p className="text-[11px] font-black text-stone-700 dark:text-stone-200">{ws.speaker}</p>
              <p className="text-[9px] text-blue-600 font-bold uppercase italic">{ws.speakerRole}</p>
            </td>

            <td className="px-6 py-5 text-center font-black text-[10px] text-stone-600 dark:text-stone-300">
              <div className="flex flex-col">
                <span>{ws.startDate === ws.endDate ? ws.startDate : `${ws.startDate} - ${ws.endDate}`}</span>
                <span className="text-[9px] text-stone-400 font-medium uppercase mt-0.5">{ws.time}</span>
              </div>
            </td>

            <td className="px-6 py-5 text-right font-black text-[11px]">Rp {ws.price.toLocaleString("id-ID")}</td>
            <td className="px-6 py-5 text-center font-black text-xs">{ws.quota}</td>
            <td className="px-6 py-5 text-center font-black text-xs text-emerald-600">{ws.soldTickets || 0}</td>
            <td className="px-6 py-5 text-center font-black text-xs text-amber-600">{ws.remainingTickets || ws.quota}</td>
            
            <td className="px-6 py-5 text-center rounded-r-xl">
              <div className={`inline-block px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-wider ${ws.statusColor}`}>
                {ws.statusLabel}
              </div>
            </td>
          </>
        )}
        emptyMessage="No workshops found."
      />

      <div className="pt-6 border-t border-stone-100 dark:border-white/5">
        <Pagination 
          totalItems={processedWorkshops.length}
          itemsPerPage={itemsPerPage}
          currentPage={workshopPage}
          onPageChange={setWorkshopPage}
        />
      </div>
    </div>
  );
};

export default WorkshopTransactionIndex;